import { TFunction } from 'i18next';
import _ from 'lodash';
import moment, { DurationInputArg1 } from 'moment';
import { Dispatch, ReactNode, SetStateAction } from 'react';
import { Category } from '../../apps/benefit/models/Categories';
import {
  ErrorState,
  ErrorType,
  ValidationError,
  ValidationErrorModel,
} from '../../apps/benefit/models/Errors';
import { resources } from '../../apps/benefit/resources/resources';
import { HttpResponse } from '../ajax';
import { customEvents } from '../constants';
import { ToastTypes } from '../enums';
import { ScrollIntoViewOptions } from '../types';
import { ToastDetail } from '../widgets/Toast';
import { config } from './config';
import { SortOption } from './models';

export const getDiscountPercentage = (
  listPrice: number,
  sellingPrice?: number,
) => {
  if (!listPrice) return 0;

  const percentage =
    (100 * (listPrice - (sellingPrice ?? listPrice))) / listPrice;
  // Correct floating-point noise before truncating to two decimal places.
  const correction = Math.sign(percentage) * Number.EPSILON * 100;
  return Math.trunc((percentage + correction) * 100) / 100;
};

export const toKebabCase = (str: string) => {
  if (str === null) return;
  return str
    .match(/[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g)
    ?.map(x => x.toLowerCase())
    .join('-');
};

export const formatDate = (date?: string, format?: string) => {
  if (!date) return '';
  return moment(date).format(format ?? config.dateFormat);
};

export const formatTime = (time?: string, format?: string) => {
  if (!time) return '';
  return moment(time).format(format ?? config.timeFormat);
};

export const formatDateTime = (dateTime?: string, format?: string) => {
  if (!dateTime) return '';
  return moment(dateTime).format(format ?? config.dateTimeFormat);
};

export const inputChecks = {
  isNumber: (input: string) => /^\d{1,9}$/.test(input.trim()),
  isDecimal: (input: string) => /^\d{1,7},\d{2}$/.test(input.trim()),
  isEmail: (input: string) =>
    // eslint-disable-next-line no-useless-escape
    /^[a-zA-Z0-9\+\.\_\%\-]{1,256}@[a-zA-Z0-9][a-zA-Z0-9\-]{0,64}(\.[a-zA-Z0-9][a-zA-Z0-9\-]{0,25})+$/.test(
      input.trim(),
    ),
};

export const pascalCaseToCamelCase = (str: string): string =>
  `${str[0].toLowerCase()}${str.substring(1)}`;

export const setValidationErrors = (
  response: HttpResponse<any>,
  setErrors: Dispatch<SetStateAction<ErrorState>>,
): void => {
  if (response.errorType !== ErrorType.ValidationError) return;
  const errors = (response.error as ValidationErrorModel).details;
  setErrors(getErrorState(errors));
};

export const getErrorState = (errors: ValidationError[]): ErrorState => {
  return errors.reduce((result, curr) => {
    const target =
      curr.target !== null ? pascalCaseToCamelCase(curr.target) : 'null';
    if (result[target]?.length) {
      result[target]!.push(curr);
    } else {
      result[target] = [curr];
    }
    return result;
  }, {} as ErrorState);
};

export const hasValidationErrors = (
  errors: ErrorState,
  field?: string,
): boolean => {
  if (field) {
    return !!errors[field]?.length;
  }
  return Object.values(errors).some(e => !!e.length);
};

export const scrollToError = (
  error: ValidationErrorModel,
  options?: ScrollIntoViewOptions,
) => {
  const newErrorState = getErrorState(error.details);
  const firstErrorKey = Object.keys(newErrorState)?.[0];
  if (firstErrorKey) {
    const opt = options ?? { behavior: 'smooth', block: 'center' };
    document.querySelector(`[name="${firstErrorKey}"]`)?.scrollIntoView(opt);
  }
};

export const unescapeCategoriesName = (root: Category): Category => {
  return {
    ...root,
    name: _.unescape(root.name),
    children: _.map(root.children, (ch: Category) => {
      return {
        ...ch,
        name: _.unescape(ch.name),
        children: _.map(ch.children, (gch: Category) => {
          return { ...gch, name: _.unescape(gch.name) };
        }),
      };
    }),
  };
};

export const getSortingOptions = (
  isDiscountVoucherList: boolean,
  isBenefitBucksList: boolean,
  isSearchResults: boolean,
) => {
  const sortOptions: SortOption[] = [
    {
      id: 0,
      resource:
        resources.flexibleBenefits.products.sortingOptions.theMostRelevant,
      showForDiscountVoucher: false,
      shoeForBenefitBucks: false,
      showForSearchResults: true,
      showByDefault: false,
    },
    {
      id: 1,
      resource:
        resources.flexibleBenefits.products.sortingOptions.theMostPopular,
      showForDiscountVoucher: true,
      shoeForBenefitBucks: true,
      showForSearchResults: true,
      showByDefault: true,
    },
    {
      id: 2,
      resource:
        resources.flexibleBenefits.products.sortingOptions.theLowestPrice,
      showForDiscountVoucher: false,
      shoeForBenefitBucks: false,
      showForSearchResults: true,
      showByDefault: true,
    },
    {
      id: 3,
      resource:
        resources.flexibleBenefits.products.sortingOptions.theHighestPrice,
      showForDiscountVoucher: false,
      shoeForBenefitBucks: false,
      showForSearchResults: true,
      showByDefault: true,
    },
    {
      id: 4,
      resource:
        resources.flexibleBenefits.products.sortingOptions.biggestDiscount,
      showForDiscountVoucher: true,
      shoeForBenefitBucks: false,
      showForSearchResults: true,
      showByDefault: true,
    },
    {
      id: 5,
      resource: resources.flexibleBenefits.products.sortingOptions.lastAdded,
      showForDiscountVoucher: true,
      shoeForBenefitBucks: true,
      showForSearchResults: true,
      showByDefault: true,
    },
  ];

  return sortOptions.filter(so => {
    if (isDiscountVoucherList) {
      return so.showForDiscountVoucher === true;
    } else if (isBenefitBucksList) {
      return so.shoeForBenefitBucks === true;
    } else if (isSearchResults) {
      return so.showForSearchResults === true;
    } else {
      return so.showByDefault === true;
    }
  });
};

const showToast = (detail: ToastDetail) => {
  document.dispatchEvent(new CustomEvent(customEvents.showToast, { detail }));
};

export const showErrorToast = (
  message: string,
  showCloseButton?: boolean,
  undoAction?: () => void,
  toastVisibilityTimeMs?: number,
  customContent?: ReactNode,
  customContentClass?: string,
) => {
  showToast({
    message,
    showCloseButton,
    toastVisibilityTimeMs,
    type: ToastTypes.Error,
    undoAction,
    customContent,
    customContentClass,
  });
};

export const showSuccessToast = (
  message: string,
  showCloseButton?: boolean,
  undoAction?: () => void,
  toastVisibilityTimeMs?: number,
  isSticky?: boolean,
  customContent?: ReactNode,
) => {
  showToast({
    message,
    showCloseButton,
    toastVisibilityTimeMs,
    type: ToastTypes.Success,
    undoAction,
    isSticky,
    customContent,
  });
};

export const showInfoToast = (
  message: string,
  showCloseButton?: boolean,
  undoAction?: () => void,
  toastVisibilityTimeMs?: number,
  customContent?: ReactNode,
) => {
  showToast({
    message,
    showCloseButton,
    toastVisibilityTimeMs,
    type: ToastTypes.Info,
    undoAction,
    customContent,
  });
};

export const showCartToast = (
  message: string,
  showCloseButton?: boolean,
  undoAction?: () => void,
  toastVisibilityTimeMs?: number,
  customContent?: ReactNode,
) => {
  showToast({
    message,
    showCloseButton,
    toastVisibilityTimeMs,
    type: ToastTypes.Cart,
    undoAction,
    customContent,
  });
};

export const getValue = (object: any, propertyName: string): any => {
  type ObjectKey = keyof typeof object;
  const propertyKey = propertyName as ObjectKey;

  return object[propertyKey];
};

export const getErrorMessage = (
  error: ValidationError,
  translate: TFunction,
): string => {
  if (error.message) return error.message;

  const placeholders = getPlaceholders(error.placeholders) as any;

  return translate(error.code, placeholders) as string;
};

/*
	getPlaceholders:
	
	input = {
		_0: any;
		_1: any;
		_2: any;
		etc.
	}
	output = {
		0: any;
		1: any;
		2: any;
		etc.
	}
*/
export const getPlaceholders = (input?: object): object => {
  if (!input) return {};

  const result = Object.entries(input).reduce((prev, [key, value]) => {
    const newKey = key.substring(1);
    return {
      ...prev,
      [newKey]: value,
    };
  }, {});

  return result;
};

export const formatPrice = (number: number | null | undefined): string => {
  if (!number) return '0';

  return number % 1 != 0
    ? (Math.round((number + Number.EPSILON) * 100) / 100).toFixed(2)
    : number.toString();
};

export const delayAsync = (ms: number) =>
  new Promise(resolve => setTimeout(resolve, ms));

export const getHostAndDomain = (url: string) => {
  const urlObject = new URL(url);

  return _.isEmpty(urlObject.port)
    ? `${urlObject.protocol}//${urlObject.hostname}`
    : `${urlObject.protocol}//${urlObject.hostname}:${urlObject.port}`;
};

export const getUniqErrorMessages = (
  messages: {
    message: string;
    onShowDetailsClick?: () => void;
    showDetailsTitle?: string;
    showDetailsColor?: string;
  }[],
) => {
  const seen = new Map<
    string,
    {
      message: string;
      onShowDetailsClick?: () => void;
      showDetailsTitle?: string;
      showDetailsColor?: string;
    }
  >();

  messages.forEach(m => {
    seen.set(m.message, m);
  });

  return Array.from(seen.values());
};

export const cleanHTMLTags = (strToSanitize: string) => {
  try {
    let myHTML = new DOMParser().parseFromString(strToSanitize, 'text/html');
    return myHTML.body.textContent ?? '';
  } catch (error) {
    return '';
  }
};

export const parseFileSize = (fileSize: number): string => {
  if (fileSize < 1048576) return `${fileSize / 1024} KB`;

  return `${fileSize / 1048576} MB`;
};

export const getRequiredValidationError = (
  target: string,
): ValidationError => ({
  target,
  code: resources.validation.required,
  severity: 'error',
  placeholders: {},
});

export const getValidationError = (
  target: string,
  code: string,
  placeholders?: object,
): ValidationError => ({
  target,
  code,
  severity: 'error',
  placeholders: placeholders ?? {},
});

export const dateAdd = (date: Date, amount?: DurationInputArg1): Date => {
  return new Date(moment(date).add(amount) as any);
};

export const toRomanianDate = (date: Date): string => {
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}.${month}.${year}`;
};

export const getExtraMonth = (month: number): number => {
  return month + 1 > 12 ? 1 : month + 1;
};

export const getExtraYear = (month: number, year: number): number => {
  return month + 1 > 12 ? year + 1 : year;
};

export const updateQueryStringParameter = (
  name: string,
  value: string | null,
): void => {
  const params = new URLSearchParams(location.search);

  if (value) {
    params.set(name, value);
  } else {
    params.delete(name);
  }

  const paramsString = params.toString();

  const newUrl = `${location.pathname}${
    paramsString ? '?' : ''
  }${paramsString}`;

  history.replaceState({}, '', newUrl);
};
